import React, { useEffect, useImperativeHandle } from "react";
import useMeasure from "react-use-measure";
import { animated, useSpring } from "react-spring";
import cx from "classnames";

import styles from "./tabbar.module.scss";

const TabBar = React.forwardRef(
  (
    {
      className,
      children,
      active,
      tabSize = "auto",
      gap = 32,
      onSelectionChange,
      showInitialAnimation = false,
    },
    ref,
  ) => {
    const [indicatorAnimations, setIndicatorAnimations] = useSpring(() => ({
      left: 0,
      width: 30,
      config: { tension: 250, friction: 30 },
    }));

    const tabBarRef = React.useRef(null);
    const tabRefs = React.useRef([]);
    const [tabItemRef, bounds] = useMeasure();

    useImperativeHandle(ref, () => {
      return {
        refresh: () => {
          calculateIndicatorPosition();
        },
      };
    }, [active]);

    useEffect(() => {
      calculateIndicatorPosition();
    }, [active, bounds]);

    const calculateIndicatorPosition = () => {
      const tab = tabRefs.current[active];
      if (!tab) return;

      const boundingRect = tab.getBoundingClientRect();
      const parentRect = tabBarRef.current?.getBoundingClientRect();
      
      if (!parentRect) return;

      const left = boundingRect.left - parentRect.left;
      const width = boundingRect.width;

      const offset = left + (width - 30) / 2;

      setIndicatorAnimations({ left: offset, width: 30 });
    };

    const handleTabClick = (index) => {
      if (onSelectionChange) {
        onSelectionChange(index);
      }
    };

    return (
      <div
        ref={tabBarRef}
        className={cx(styles.tabBar, styles[tabSize], className)}
        style={{ gap: `${gap}px` }}
      >
        {React.Children.map(children, (child, index) => {
          if (!child) return null;
          
          return (
            <div
              ref={(el) => {
                tabRefs.current[index] = el;
                if (index === 0) tabItemRef(el);
              }}
              onClick={() => handleTabClick(index)}
              style={{ cursor: 'pointer' }}
            >
              {child}
            </div>
          );
        })}
        
        <animated.div
          className={styles.indicator}
          style={{
            left: indicatorAnimations.left,
            width: indicatorAnimations.width,
          }}
        />
      </div>
    );
  },
);

TabBar.displayName = "TabBar";

export { TabBar };